// Copyright (c) Hamilton Manufacturing Corp.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { QuestRoute } from '../_common/api/QuestRoutes';
import { between, gradient } from '../_common/api/utility';
import Counter from '../_common/Counter';
import Icon from '../_common/form/Icon';
import Title from '../_common/form/Title';
import { useGlobalState } from '../_common/globalState/GlobalStateProvider';
import Modal from '../_common/Modal';
import { HamQuest } from '../../App';

const Wrapper = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0.3em 0;
    margin: 0 0 0.5em 0;
    border-radius: 1rem;
    color: ${({ theme }) => theme.mobile.loyaltyCard.text};
    background: ${({ theme }) => theme.mobile.loyaltyCard.background};
    text-align: center;
    box-shadow: 1px 1px 5px 1px ${({ theme }) => theme.mobile.shadow};
`
const Header = styled.div`
    font-weight: bold;
    font-size: 1.3rem;
`
const SpacedRow = styled.div`
    display: flex;
    justify-content: space-around;
    width: 100%;
`
const BarWrapper = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.2em 0;
`
const ParentBar = styled.div`
    position: relative;
    width: 90%;
    height: 20px;
    border-radius: 2rem;
    margin: 0.5rem;
    background: ${({ theme }) => theme.mobile.loyaltyCard.parentBar};
    border: 3px solid ${({ theme }) => theme.mobile.loyaltyCard.parentBar};
    // box-shadow: 0px 0px 3px 5px ${({ theme }) => theme.mobile.shadow};

    ${({ loading }) => loading && `
        background-size: 30px 30px;
        background-image: linear-gradient(135deg, #ffffff26 25%, transparent 25%, transparent 50%, #ffffff26 50%, #ffffff26 75%, transparent 75%, transparent);
        animation: barberpole 1s linear infinite;
    `}
`
const StatusCar = styled(Icon)`
    position: absolute;
    left: calc(${({ pos }) => pos}%);
    color: ${({ theme }) => theme.mobile.loyaltyCard.car};
    font-size: 20px;
    animation: car 0.5s infinite;
    transition: all 0.5s;
`;

const StatusBar = styled(ParentBar)`
    width: ${({ width }) => width}%;
    position: absolute;
    background: ${({ theme, ratio }) => {
        // gradient(theme.mobile.loyaltyCard.bar, theme.mobile.loyaltyCard.barTo, ratio)
        return theme.mobile.loyaltyCard.bar
    }}cc;
    box-shadow: 0px 0px 3px 0px ${({ theme }) => theme.mobile.loyaltyCard.bar};

    border: unset;

    margin: 0;
    transition: all 0.5s;
    &:before {
        content: '';
        display: block;
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
        border-radius: 2rem;

        ${({ bubbles }) => bubbles ? `
        background: url(https://s3-us-west-2.amazonaws.com/s.cdpn.io/172299/bubbles-mask.gif);
        mix-blend-mode: overlay;
        opacity: 0.5;
        ` : null}
    }
`

const Heart = styled(Icon)`
    font-size: 3em;
    padding: 0.2em;
    color: ${({ theme }) => theme.error}
`
const Col = styled.div`
    display: flex;
    flex-direction: column;
    margin: 0 0.2em;
`
const Row = styled(Link)`
    display: flex;
    align-items: center;
    text-decoration: none;
    color: inherit;
`

const LoyaltyCard = ({ ...props }) => {
    const {
        state: {
            active_user,
            site_config
        },
    } = useGlobalState();

    const [points, setPoints] = useState(null)
    const [goal, setGoal] = useState(null)
    const [title, setTitle] = useState(' ')

    const callPoints = () => {
        HamQuest.get(QuestRoute.user.loyaltyPoints)
            .then(res => {
                if (window.location.href.match('#MAC')) {
                    setPoints(res.point_goal / 2)
                } else {
                    setPoints(res.current_points)
                }
                setGoal(res.point_goal)
                setTitle(res.widget_title)
            })
    }

    useEffect(() => {
        if (!active_user.isLoggedIn) return;
        callPoints()
    }, [active_user.accessToken])

    const showInfo = () => {
        Modal.Show({
            title: 'Free Wash Status',
            body: <div>
                Earn points every time you make an in-app purchase and work your way towards a FREE wash!
                <br />
                <br />
                Every time you buy a wash or use AirPAY, your loyalty tracker will update with a current points total. Reach {goal?.toLocaleString()} points to receive a <b>FREE</b> {title}!
                <br />
                <br />
                Your Points: {points ? <Counter to={points} /> : 0} / {goal?.toLocaleString() || 0}
                <br />
                <br />
                You'll receive a notification of your reward and can redeem it by going to your Wallet.
            </div>
        })
    }

    return active_user.isLoggedIn ? (
        <Wrapper onClick={showInfo}>
            {
                window.innerHeight > 650 ?
                    <Header>Reward Progress</Header>
                    : null
            }
            {
                window.innerHeight > 680 ?
                    <div style={{ width: '70%', fontSize: '0.9em' }}>
                        Reach {goal?.toLocaleString()} points
                        <br />
                        to receive a <b>FREE</b> {title}
                    </div>
                    : window.innerHeight > 620 ?
                        <div style={{ width: '80%', fontSize: '0.9em' }}>
                            Earn <b>{(goal - points)?.toLocaleString()}</b> more points
                            <br />
                            to receive a <b>FREE</b> {title}
                        </div>
                        : null
            }
            <BarWrapper>
                <ParentBar loading={points === null ? 1 : 0} >
                    <StatusCar icon='sideCar' pos={between(((points / goal) * 100) || 6, 6, 92)} />
                    <StatusBar width={between(((points / goal) * 100 || 6), 6, 100)} ratio={points / goal} bubbles={site_config.bubbles.enabled} />
                </ParentBar>
            </BarWrapper>
            {
                window.innerHeight > 680 ?
                    <b>{points ? <Counter to={points} /> : 0} / {goal?.toLocaleString() || 0}</b>
                    : null
            }
        </Wrapper>
    ) : (
        <Wrapper>
            <Row to='/logIn'>
                <Heart icon='heart' />
                <Col>
                    <Title>SIGN UP TO USE OUR LOYALTY PROGRAM!</Title>
                    <div>Get points with every purchase and earn a free wash!</div>
                </Col>
            </Row>
        </Wrapper>
    );
}

export default LoyaltyCard;